/**
 * /settings/scorecard-templates — Settings → Hiring → Scorecard
 * templates (Wave K1.6; SOP §8.3 — catalog admin lives in Settings).
 *
 * List/create/edit/delete the tenant's interview scorecard templates.
 * Mutations are HR-admin + CSRF + Audit server-side; the page UX-gates
 * to HR admins (backend re-checks). Deleting a template that is used by
 * one or more interviews is refused server-side (409 `template_in_use`)
 * — the toast surfaces the message.
 */

import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import {
  useDeleteScorecardTemplate,
  useScorecardTemplates,
} from "@/hooks/use-hiring";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { ScorecardTemplateForm } from "@/components/hiring/scorecard-template-form";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type { ScorecardTemplate } from "@/types/hiring";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/scorecard-templates",
)({
  component: SettingsScorecardTemplatesRoute,
});

function SettingsScorecardTemplatesRoute() {
  const { isHrAdmin } = useHrRoles();
  const q = useScorecardTemplates();
  const del = useDeleteScorecardTemplate();

  const templates = useMemo(
    () =>
      [...(q.data?.scorecard_templates ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [q.data],
  );

  async function onDelete(t: ScorecardTemplate) {
    try {
      await del.mutateAsync(t.id);
      toast.success(`Deleted "${t.name}".`);
    } catch (err) {
      // A 409 template_in_use lands here — the template is referenced
      // by interviews and stays; the message says so.
      toast.error(toApiError(err).message);
    }
  }

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">
                Scorecard Templates
              </h2>
              <p className="text-sm text-muted-foreground">
                Named attribute sets interviewers rate 1–5 — pick one
                when scheduling an interview; the feedback form renders
                a row per attribute.
              </p>
            </div>
            {isHrAdmin && <ScorecardTemplateForm />}
          </div>

          {!isHrAdmin && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR managers and company admins can change scorecard
                templates.
              </AlertDescription>
            </Alert>
          )}

          {q.isLoading && (
            <div className="space-y-3">
              {["s1", "s2", "s3"].map((k) => (
                <Skeleton key={k} className="h-20 w-full" />
              ))}
            </div>
          )}

          {q.isError && (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn&apos;t load scorecard templates
              </AlertTitle>
              <AlertDescription>
                {toApiError(q.error).message}
              </AlertDescription>
            </Alert>
          )}

          {q.data && templates.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                No scorecard templates yet.
                {isHrAdmin &&
                  " Create one to give interviewers a structured 1–5 sheet."}
              </CardContent>
            </Card>
          )}

          {q.data && templates.length > 0 && (
            <div className="space-y-3">
              {templates.map((t) => (
                <Card key={t.id}>
                  <CardHeader>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <CardTitle className="text-base">
                          {t.name}
                        </CardTitle>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {t.attributes.map((a) => (
                            <Badge key={a.key} variant="secondary">
                              {a.label}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {isHrAdmin && (
                        <div className="flex shrink-0 gap-2">
                          <ScorecardTemplateForm existing={t} />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1.5 text-destructive"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Delete &ldquo;{t.name}&rdquo;?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  A template that is already used by an
                                  interview can&apos;t be deleted — the
                                  server will refuse and keep it.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => void onDelete(t)}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      </SettingsShell>
    </>
  );
}
